import React, { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQuery } from '@apollo/client';
import { Loader2, Plus, Truck, X } from 'lucide-react';
import { DeliveryProfile } from '@shopify-multiseller/graphql/partner/types';
import { Badge, Button, DialogOverlay } from '../primitives';
import { getCountryName } from '../../helpers/country';
import { GET_DELIVERY_PROFILES } from './queries';

interface DeliveryProfileSelectorProps {
  open: boolean;
  onClose: () => void;
  selectedProfileId?: string;
  onSelect: (_profileId: string) => void;
  onAdd: () => void;
  onEdit: (_profileId: string) => void;
}

/** How long the seller takes to get an order out, said in words. */
const processingText = (profile: DeliveryProfile): string | undefined => {
  const { min, max, unit } = profile.processingTime ?? {};
  if (min == null && max == null) return undefined;
  const unitText = String(unit ?? '').toLowerCase() || 'days';
  return min === max ? `${min} ${unitText}` : `${min}–${max} ${unitText}`;
};

/**
 * Picking a saved profile to apply to a product.
 *
 * Rebuilt alongside the editor it sits next to. It had the same faults and one
 * of its own: it was six hundred pixels tall whatever it held, so a seller with
 * one profile got a card and half a screen of nothing, and a seller with none
 * got the empty half on its own with no explanation of what was missing.
 */
export const DeliveryProfileSelector: React.FC<DeliveryProfileSelectorProps> = ({
  open,
  onClose,
  onSelect,
  selectedProfileId,
  onAdd,
  onEdit,
}) => {
  const [selectedProfile, setSelectedProfile] = useState<DeliveryProfile | undefined>();
  const [otherProfiles, setOtherProfiles] = useState<DeliveryProfile[]>([]);

  const { data, loading } = useQuery<{
    deliveryProfiles: DeliveryProfile[];
  }>(GET_DELIVERY_PROFILES, {
    fetchPolicy: 'network-only',
    skip: !open,
  });

  useEffect(() => {
    if (data?.deliveryProfiles) {
      const selected = data.deliveryProfiles.find((profile) => profile.id === selectedProfileId);
      const others = data.deliveryProfiles.filter((profile) => profile.id !== selectedProfileId);
      setSelectedProfile(selected);
      setOtherProfiles(others);
    }
  }, [data?.deliveryProfiles, selectedProfileId]);

  const nothingToShow = !loading && !selectedProfile && otherProfiles.length === 0;

  const renderProfile = (profile: DeliveryProfile, isSelected = false) => (
    <div
      key={profile.id}
      data-testid="delivery-profile-option"
      className={
        isSelected
          ? 'flex items-center gap-3 rounded-lg border border-primary bg-primary/5 p-4'
          : 'flex items-center gap-3 rounded-lg border border-border p-4'
      }
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-medium text-foreground">
            {/* An unnamed profile belongs to one product and was never saved
                for reuse; "Custom Profile" said that in title case and left
                the reader to work out whose it was. */}
            {profile.name || 'This product’s own rates'}
          </h3>
          {isSelected && <Badge variant="success">In use</Badge>}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {[
            `Ships from ${getCountryName(profile.originCountryCode)}`,
            processingText(profile),
            profile.zones?.length ? `${profile.zones.length} zones` : undefined,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      <div className="ml-auto shrink-0">
        {isSelected ? (
          <Button variant="outline" size="sm" onClick={() => onEdit(profile.id)}>
            Edit
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => onSelect(profile.id)}>
            Use this
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogOverlay className="z-[100]" />
        <DialogPrimitive.Content
          aria-describedby="delivery-profile-selector-purpose"
          className="fixed left-1/2 top-1/2 z-[100] flex max-h-[80vh] w-[min(34rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl focus:outline-none"
        >
          <div className="flex items-start gap-3 border-b border-border px-6 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title asChild>
                <h2 className="text-base font-semibold text-foreground">Delivery profile</h2>
              </DialogPrimitive.Title>
              <p
                id="delivery-profile-selector-purpose"
                className="mt-0.5 text-xs text-muted-foreground"
              >
                Choose the rates this product ships on.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="ml-auto rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {loading ? (
              <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Loading profiles…
              </div>
            ) : nothingToShow ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <Truck className="h-6 w-6 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">No saved profiles yet</p>
                <p className="max-w-xs text-xs text-muted-foreground">
                  A profile holds your origin, processing time and rates, and can be applied to any
                  number of products.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedProfile && renderProfile(selectedProfile, true)}
                {otherProfiles.map((profile) => renderProfile(profile))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 border-t border-border bg-muted/40 px-6 py-3">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button className="ml-auto" onClick={onAdd} icon={<Plus className="h-4 w-4" />}>
              Create a profile
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
